/* eslint-disable */

import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  TablePagination, // เพิ่ม Import สำหรับ Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';

// Import ของ MUI DatePicker และ dayjs
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

import api from '../utils/functions/api';
import { getStorage } from '../utils/functions/localstorage';

function RecipientReport() {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // เพิ่ม State สำหรับ Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchReport = async () => {
    if (!startDate || !endDate) {
      setError('กรุณาเลือกวันที่เริ่มต้นและวันที่สิ้นสุด');
      return;
    }

    setLoading(true);
    setError(null);

    const apiStartDate = startDate.format('YYYYMMDD');
    const apiEndDate = endDate.format('YYYYMMDD');

    try {
      const path = `${process.env.REACT_APP_APIHOST}/recipient_report?start_date=${apiStartDate}&end_date=${apiEndDate}`;

      const response = await api.get(path, {
        headers: { Authorization: getStorage('token') },
      });

      const result = response.data;

      if (result.success) {
        setReportData(result.data || []);
        setPage(0); // รีเซ็ตกลับไปหน้าแรกเสมอเมื่อค้นหาข้อมูลใหม่สำเร็จ
      } else {
        throw new Error(result.message || 'เกิดข้อผิดพลาดในการดึงข้อมูล');
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        'เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์';
      setError(errorMessage);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = () => {
    if (!reportData || reportData.length === 0) return;

    const startStr = startDate.format('YYYY-MM-DD');
    const endStr = endDate.format('YYYY-MM-DD');

    const titleRow = `Recipient Report ${startStr} - ${endStr},,,,,,,,`;

    const headers = [
      'Company Name',
      'Recipient ID',
      'Order ID',
      'Payment Ref ID',
      'Transaction Date',
      'Transaction Time',
      'Amount',
      'Fee',
      'Total',
    ];

    const csvRows = reportData.map((row) => {
      return [
        `"${row.company_name || '-'}"`,
        `"${row.recipient_id || '-'}"`,
        `"${row.order_id || '-'}"`,
        `"${row.payment_ref_id || '-'}"`,
        `"${row.transaction_date || '-'}"`,
        `"${row.transaction_time || '-'}"`,
        `"${row.amount || '0'}"`,
        `"${row.fee || '0'}"`,
        `"${row.total || '0'}"`,
      ].join(',');
    });

    const csvString = [titleRow, headers.join(','), ...csvRows].join('\n');

    const blob = new Blob(['\uFEFF' + csvString], {
      type: 'text/csv;charset=utf-8;',
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `recipient_report_${startStr}_to_${endStr}.csv`,
    );
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ฟังก์ชันจัดการเมื่อเปลี่ยนหน้า
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // ฟังก์ชันจัดการเมื่อเปลี่ยนจำนวนแถวต่อหน้า
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // กลับไปหน้าแรกเสมอเมื่อเปลี่ยนจำนวนแถว
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            fontWeight="600"
            color="text.primary"
            gutterBottom
          >
            Recipient Report
          </Typography>
        </Box>
      </Box>

      {/* Filter Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <DatePicker
                label="วันที่เริ่มต้น"
                inputFormat="YYYY-MM-DD"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                renderInput={(params) => (
                  <TextField {...params} size="small" sx={{ minWidth: 200 }} />
                )}
              />
              <DatePicker
                label="วันที่สิ้นสุด"
                inputFormat="YYYY-MM-DD"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                minDate={startDate}
                renderInput={(params) => (
                  <TextField {...params} size="small" sx={{ minWidth: 200 }} />
                )}
              />
              <Button
                variant="contained"
                disableElevation
                onClick={fetchReport}
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SearchIcon />
                  )
                }
                sx={{
                  height: 40,
                  px: 4,
                  borderRadius: 1.5,
                  textTransform: 'none',
                }}
              >
                {loading ? 'กำลังค้นหา...' : 'Search Report'}
              </Button>
            </Box>
          </LocalizationProvider>

          {reportData && reportData.length > 0 && (
            <Button
              variant="outlined"
              color="success"
              onClick={handleDownloadExcel}
              startIcon={<DownloadIcon />}
              sx={{
                height: 40,
                px: 3,
                borderRadius: 1.5,
                textTransform: 'none',
              }}
            >
              Download Excel
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 3, borderRadius: 1.5 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Data Table Section */}
      {reportData && (
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <TableContainer>
            <Table sx={{ minWidth: 1000 }} aria-label="recipient report table">
              <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Company Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Recipient ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    Order ID / Ref ID
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date & Time</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Amount
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Fee
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.length > 0 ? (
                  // ใช้ .slice เพื่อเลือกข้อมูลที่จะนำมาแสดงตามหน้าปัจจุบันและจำนวนแถว
                  reportData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => (
                      <TableRow key={`${row.order_id}-${index}`} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {row.company_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.payment_ref_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.recipient_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="500"
                            color="primary.main"
                          >
                            {row.order_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.transaction_date}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.transaction_time}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{row.amount}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{ color: 'error.main' }}
                          >
                            -{row.fee}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: 'success.main' }}
                          >
                            {row.total}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary">
                        ไม่พบข้อมูลรายงานในช่วงเวลาที่เลือก
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Table Pagination Section */}
          {reportData.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={reportData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="รายการต่อหน้า:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} จาก ${count !== -1 ? count : `มากกว่า ${to}`}`
              }
            />
          )}
        </Paper>
      )}
    </Container>
  );
}

export default RecipientReport;
